import {
    Box,
    Toolbar
} from '@mui/material';

import { PublicPropertyDashboardMainBar } from "./PublicPropertyDashboardMainBar";
import { TeneraPropertyDashboard } from '../../components/dashboard/pages/TeneraPropertyDashboard';
import { ThemeProvider } from "@mui/material";
import { staticDashboardService } from "../../lib/dashboard/StaticDashboardService";
import { theme } from '../../assets/style/dashboardTheme';
import { usePageDefaultStyle } from '../../assets/style/usePageDefaultStyle';
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";

interface RouteParams {
    propertyId: string;
}

export const REFETCH_POLLING_TIMES_MS = 60 * 1000; // 60 seconds

/**
 * Public Property Dashboard Page Component
 * Displays property data in a dashboard format
 */
export const PublicPropertyDashboardPage = (): JSX.Element => {
    // Extract propertyId from URL parameters
    const { propertyId } = useParams<RouteParams>();

    const { data: staticDashboard, error, isLoading } = useQuery({
        queryKey: ["staticDashboard", propertyId],
        queryFn: async () => {
            return await staticDashboardService.get(propertyId);
        },
        refetchInterval: REFETCH_POLLING_TIMES_MS,
        refetchIntervalInBackground: true,
        enabled: !!propertyId
    });
    const { pageDefaultStyle } = usePageDefaultStyle();

    if (error) {
        return <div>Error: {error instanceof Error ? error.message : "Failed to fetch dashboard data"}</div>;
    }

    if (isLoading || !staticDashboard?.data) {
        return <div>Loading...</div>;
    }

    const dashboardData = staticDashboard.data;
    const updatedTime = dashboardData.updatedTimestamp ? new Date(dashboardData.updatedTimestamp) : undefined;

    // Render dashboard with data
    return (
        <ThemeProvider theme={theme}>
            <PublicPropertyDashboardMainBar updatedTime={updatedTime} />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    pt: 6
                }}
            />
            <Toolbar />
            <Box sx={pageDefaultStyle}>
                <TeneraPropertyDashboard
                    property={dashboardData.property}
                    locations={dashboardData.locations}
                    issues={dashboardData.issues}
                    images={dashboardData.images}
                    assessments={dashboardData.assessments}
                    assessmentSchedules={dashboardData.assessmentSchedules}
                    resourceAssessmentScheduleAssociations={
                        dashboardData.resourceAssessmentScheduleAssociations
                    }
                    linkRegistries={dashboardData.linkRegistries}
                    issueSubmissionNotificationSetting={dashboardData.issueNotificationSetting}
                    contacts={dashboardData.contacts}
                    fetchAssessmentScheduleStatus="success"
                    tags={dashboardData.tags ?? []}
                />
            </Box>
        </ThemeProvider>
    );
};